import React, { useEffect, useRef, useMemo } from 'react';

interface TranscriptOverlayProps {
  sectionText: string;
  literalText?: string;
  progress: number;
  duration: number;
  isPlaying: boolean;
  chapterTitle: string;
  sectionNum: number;
  sectionCount: number;
  onClose: () => void;
}

const TranscriptOverlay: React.FC<TranscriptOverlayProps> = ({
  sectionText,
  literalText,
  progress,
  duration,
  isPlaying,
  chapterTitle,
  sectionNum,
  sectionCount,
  onClose,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const prevTextRef = useRef(sectionText);

  // Split text into paragraphs for natural rendering
  const paragraphs = useMemo(() => {
    if (!sectionText) return [];
    return sectionText.split(/\n\n+/).filter(Boolean);
  }, [sectionText]);

  // Smooth scroll based on playback progress
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || !isPlaying || duration <= 0) return;

    const fraction = Math.max(0, Math.min(1, progress / duration));
    const maxScroll = el.scrollHeight - el.clientHeight;
    if (maxScroll <= 0) return;

    el.scrollTo({ top: fraction * maxScroll, behavior: 'smooth' });
  }, [progress, duration, isPlaying]);

  // Reset scroll when section text changes
  useEffect(() => {
    if (sectionText !== prevTextRef.current) {
      prevTextRef.current = sectionText;
      bodyRef.current?.scrollTo({ top: 0 });
    }
  }, [sectionText]);

  return (
    <div className="transcript-overlay transcript-overlay--visible">
      {/* Header */}
      <div className="transcript-overlay__header">
        <div>
          <span style={{ color: '#fff', fontSize: '0.875rem', fontWeight: 500 }}>
            {chapterTitle}
          </span>
          <span style={{ color: '#a7a7a7', fontSize: '0.8125rem', marginLeft: '12px' }}>
            Section {sectionNum} of {sectionCount}
          </span>
        </div>
        <button
          onClick={onClose}
          className="sp-control-btn"
          style={{ width: 32, height: 32 }}
          aria-label="Close transcript"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      </div>

      {/* Scrollable text body with focus-window mask */}
      <div className="transcript-overlay__body" ref={bodyRef}>
        {/* Spacer so first lines can scroll into the bright center */}
        <div style={{ height: '40vh' }} />
        <div className="transcript-overlay__text">
          {literalText && (
            <>
              {literalText.split(/\n\n+/).filter(Boolean).map((para, i) => (
                <p key={`lit-${i}`} className="mb-6" style={{ fontStyle: 'italic', color: '#d4c5a0' }}>
                  {para}
                </p>
              ))}
              <hr style={{ border: 'none', borderTop: '1px solid #444', margin: '1.5rem 0' }} />
            </>
          )}
          {paragraphs.map((para, i) => (
            <p key={i} className="mb-6">
              {para}
            </p>
          ))}
        </div>
        {/* Spacer so last lines can scroll into the bright center */}
        <div style={{ height: '40vh' }} />
      </div>
    </div>
  );
};

export default TranscriptOverlay;
